import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, CheckCircle2, AlertCircle } from 'lucide-react';

interface ExecutiveCashPositionCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

export function ExecutiveCashPositionCardThumbnail({ isHovered = false, alwaysVisible = false }: ExecutiveCashPositionCardThumbnailProps) {
  // When always visible, show at 40% scale. When hover-only, show at 100% scale (or 2.5x on hover)
  const baseScale = alwaysVisible ? 'scale-[0.4]' : 'scale-100';
  const scale = isHovered ? 'scale-100' : baseScale;
  const zIndex = isHovered ? 'z-50' : 'z-0';
  
  return (
    <div className={`transition-all duration-300 ${scale} ${zIndex} origin-left`}>
      <Card className="w-[300px] border-2 shadow-sm bg-background">
        <CardHeader className="px-3 pt-3 pb-2">
          <CardTitle className="text-[11px] font-semibold">Cash Position</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3 space-y-2">
          {/* Current Cash - Large Display */}
          <div className="text-center space-y-0.5 p-1.5 rounded">
            <div className="text-2xl font-bold">£287,450</div>
            <div className="text-[8px] text-muted-foreground">Current Cash Balance</div>
          </div>

          {/* Health Status */}
          <div className="flex items-center gap-1.5 p-1.5 bg-muted rounded text-[8px]">
            <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-3 w-3 text-white" />
            </div>
            <div>
              <div className="font-semibold text-[9px]">Healthy</div>
              <div className="text-muted-foreground">13.7 months coverage</div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 gap-1.5">
            <div className="text-center p-1.5 bg-muted rounded text-[8px]">
              <div className="text-[11px] font-bold">£312,890</div>
              <div className="text-muted-foreground mt-0.5">30-Day Forecast</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded text-[8px]">
              <div className="text-[11px] font-bold">£298,120</div>
              <div className="text-muted-foreground mt-0.5">90-Day Forecast</div>
            </div>
          </div>

          {/* Alert */}
          <div className="p-1.5 bg-amber-50 border border-amber-200 rounded text-[7px]">
            <div className="font-semibold flex items-center gap-1 mb-0.5">
              <AlertCircle className="h-2 w-2 text-amber-500" />
              3 Overdue Invoices
            </div>
            <div className="text-muted-foreground">£24,500 requiring attention</div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-1.5 pt-1.5 border-t">
            <div className="text-[7px]">
              <div className="text-muted-foreground">Overdue</div>
              <div className="text-[9px] font-semibold">3</div>
              <div className="text-muted-foreground">£24,500</div>
            </div>
            <div className="text-[7px]">
              <div className="text-muted-foreground">Upcoming</div>
              <div className="text-[9px] font-semibold">12</div>
              <div className="text-muted-foreground">£89,200</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
